import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

const DailyHadithCard = ({ dailyHadith, onNext, onPrevious, hideTitle, isLoading, error, noCard = false }) => {
  const { isDarkMode } = useDarkMode();
  
  // Safe handlers to prevent errors if props are missing
  const handleNext = (e) => {
    e.preventDefault();
    console.log('[DailyHadithCard] Next button clicked', { hasHandler: typeof onNext === 'function' });
    if (typeof onNext === 'function') {
      try {
        onNext();
      } catch (error) {
        console.error("[DailyHadithCard] Error in onNext handler:", error);
      }
    } else {
      console.warn("[DailyHadithCard] onNext handler is not defined");
    }
  };

  const handlePrevious = (e) => {
    e.preventDefault();
    console.log('[DailyHadithCard] Previous button clicked', { hasHandler: typeof onPrevious === 'function' });
    if (typeof onPrevious === 'function') {
      try {
        onPrevious();
      } catch (error) {
        console.error("[DailyHadithCard] Error in onPrevious handler:", error);
      }
    } else {
      console.warn("[DailyHadithCard] onPrevious handler is not defined");
    }
  };

  const renderContent = () => {
    // Handle loading state
    if (isLoading) {
      return (
        <div className="animate-pulse space-y-8">
          {/* Arabic text placeholder */}
          <div className="relative mb-8">
            <div className="h-32 bg-gray-200 dark:bg-gray-800 rounded-lg" />
          </div>
          {/* Translation placeholder */}
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-3/4 mx-auto" />
            <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/2 mx-auto" />
          </div>
        </div>
      );
    }
    
    // Handle error state
    if (error) {
      console.log('[DailyHadithCard] Showing error state:', error);
      return (
        <div className="space-y-4 text-center">
          <div className={`p-4 rounded-lg ${
            isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'
          }`}>
            <p>Unable to load daily hadith. Please try again later.</p>
          </div>
        </div>
      );
    }
    
    // Handle missing data state
    if (!dailyHadith) {
      console.log('[DailyHadithCard] No data available');
      return (
        <div className="space-y-4 text-center">
          <div className={`p-4 rounded-lg ${
            isDarkMode ? 'bg-amber-900/20 text-amber-400' : 'bg-amber-50 text-amber-600'
          }`}>
            <p>No daily hadith available at this time.</p>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Arabic text with decorative elements */}
        <div className="relative mb-8">
          <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-20 h-px
            bg-gradient-to-r from-transparent ${
              isDarkMode 
                ? 'via-emerald-500/50' 
                : 'via-emerald-600/30'
            } to-transparent`} />
          <p className={`text-4xl text-center font-['Decotype_Thuluth'] leading-loose py-2 mt-8 mb-8 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            {dailyHadith.arabic}
          </p>
          <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-px
            bg-gradient-to-r from-transparent ${
              isDarkMode 
                ? 'via-emerald-500/50' 
                : 'via-emerald-600/30'
            } to-transparent`} />
        </div>

        {/* Translation with elegant typography */}
        <div className="space-y-4">
          <p className={`text-center text-lg font-light leading-relaxed ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {dailyHadith.translation}
          </p>
          {dailyHadith.source && (
            <p className={`text-center text-sm font-medium ${
              isDarkMode ? 'text-emerald-400' : 'text-emerald-700'
            }`}>
              {dailyHadith.source}
            </p>
          )}
        </div>
      </>
    );
  };
  
  // Component to render the full content including header and footer
  const HadithContent = () => (
    <>
      {/* Header */}
      <div className="relative px-4 pt-4 pb-2">
        <h2 className={`text-lg font-medium ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Daily Hadith
        </h2>
      </div>

      {/* Content section */}
      <div className={`relative px-4 py-6 ${
        isDarkMode 
          ? '' 
          : 'bg-gradient-to-b from-white/40 to-transparent'
      }`}>
        {renderContent()}
      </div>

      {/* Footer navigation */}
      <div className={`px-4 py-4 border-t ${
        isDarkMode ? 'border-white/5' : 'border-gray-100'
      }`}>
        <div className="flex justify-between items-center">
          <button 
            onClick={handlePrevious}
            disabled={isLoading}
            className={`flex items-center space-x-2 transition-colors ${
              isDarkMode 
                ? 'text-gray-400 hover:text-emerald-400'
                : 'text-gray-500 hover:text-emerald-700'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <CaretLeft size={20} weight="thin" />
            <span className="text-sm">Previous</span>
          </button>
          <button 
            onClick={handleNext}
            disabled={isLoading}
            className={`flex items-center space-x-2 transition-colors ${
              isDarkMode 
                ? 'text-gray-400 hover:text-emerald-400'
                : 'text-gray-500 hover:text-emerald-700'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span className="text-sm">Next</span>
            <CaretRight size={20} weight="thin" />
          </button>
        </div>
      </div>
    </>
  );
  
  // If noCard is true, render contents directly
  if (noCard) {
    return <HadithContent />;
  }
  
  // Otherwise wrap in container
  return (
    <div className="relative">
      <div className={`relative rounded-2xl overflow-hidden backdrop-blur-xl ${
        isDarkMode 
          ? 'bg-white/5' 
          : 'bg-white/60 shadow-[inset_0_0_1px_1px_rgba(255,255,255,0.9)]'
      }`}>
        <HadithContent />
      </div>
    </div>
  );
};

export default DailyHadithCard;